import {
  bookingNetProfitPaise,
  ROUND_OFF_LABEL,
  type TravelBookingAdminDetail,
  type TravelBookingAdminEvent,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzCard,
  UzDescriptionList,
  UzErrorState,
  UzSkeleton,
} from '@utazzo/ui';
import {
  ArrowLeft,
  Ban,
  BedDouble,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Mail,
  Plane,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  agoLabel,
  dateLabel,
  dateTimeLabel,
  eventLabel,
  money,
  signedMoney,
  untilLabel,
} from '../booking-format';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { BookNowDialog } from '../components/BookNowDialog';
import { CancelBookingDialog } from '../components/CancelBookingDialog';
import { CancellationNoticeDialog } from '../components/CancellationNoticeDialog';
import { RefundDialog } from '../components/RefundDialog';
import { ResendConfirmationDialog } from '../components/ResendConfirmationDialog';
import { TravellersCard } from '../components/TravellersCard';
import { useDownloadEticket, useTravelBooking } from '../use-travel-bookings';

import { PageShell } from '@/components/page-shell';

/** The list URLs a row can be opened from; anything else falls back to Action needed. */
const BACK_DEFAULT = '/ticketing/action';
const isListPath = (p: string): boolean => p.startsWith('/ticketing') || p.startsWith('/bookings');

/**
 * One website booking, and the buttons that matter on it.
 *
 * Laid out for the operator who arrived from a notification: the hold panel
 * with the supplier's real reason sits at the top, the actions are in the
 * header, and everything else - travellers, money, supplier identifiers,
 * the timeline - is below for the support call that follows. Nothing here is
 * editable: the guest filled it in before paying. The decisions are book or
 * refund on a hold, cancel or resend on a confirmed booking, and refund on a
 * cancelled one. A `?notice=cancelled` in the URL (from the "cancelled -
 * refund the guest" notification) opens the cancellation summary on top.
 */
export const TravelBookingDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookNow, setBookNow] = useState(false);
  // Where "back" goes: the list URL the row was opened from (tab included),
  // when there is one. A notification or a pasted link lands on the default.
  const from = (location.state as { from?: unknown } | null)?.from;
  const backTo = typeof from === 'string' && isListPath(from) ? from : BACK_DEFAULT;
  const [params, setParams] = useSearchParams();
  const [refund, setRefund] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [resend, setResend] = useState(false);
  const [notice, setNotice] = useState(false);
  const [poll, setPoll] = useState(false);
  const booking = useTravelBooking(id ?? null, { poll });
  const downloadEticket = useDownloadEticket();

  // Poll while the supplier answer is still settling, and stop the moment it
  // lands.
  useEffect(() => {
    setPoll(booking.data?.status === 'BOOKING');
  }, [booking.data?.status]);

  // Arrived from the cancellation notification: show the summary first, then
  // drop the flag from the URL so a refresh does not re-open it.
  useEffect(() => {
    if (params.get('notice') !== 'cancelled') return;
    setNotice(true);
    const next = new URLSearchParams(params);
    next.delete('notice');
    setParams(next, { replace: true });
  }, [params, setParams]);

  if (!id) {
    navigate(BACK_DEFAULT, { replace: true });
    return <></>;
  }

  if (booking.isLoading) {
    return (
      <PageShell title={<UzSkeleton className="h-7 w-56" />}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <UzSkeleton className="h-40 lg:col-span-2" />
          <UzSkeleton className="h-40" />
          <UzSkeleton className="h-64 lg:col-span-3" />
        </div>
      </PageShell>
    );
  }

  if (booking.isError || !booking.data) {
    return (
      <PageShell title="Booking">
        <UzErrorState
          title="Could not load this booking"
          description="It may have been removed, or the server returned an error."
          action={
            <UzButton variant="outline" onClick={() => booking.refetch()}>
              Try again
            </UzButton>
          }
        />
      </PageShell>
    );
  }

  const b = booking.data;
  const onHold = b.status === 'ON_HOLD';
  const confirmed = b.status === 'CONFIRMED';
  const moneyLeft = BigInt(b.sellPaise) - BigInt(b.refundedPaise) > 0n && b.paidAt !== null;
  // Refund is offered wherever guest money is still with us and the booking
  // is not mid-flight: on hold, cancelled, failed, partly refunded - and on
  // a live confirmed booking too (a goodwill refund does not cancel it).
  const canRefund =
    moneyLeft &&
    (onHold ||
      b.status === 'CANCELLED' ||
      b.status === 'BOOK_FAILED' ||
      b.status === 'REFUNDED' ||
      confirmed);
  const refresh = (): void => {
    void booking.refetch();
  };

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {b.product === 'FLIGHT' ? (
            <Plane className="h-5 w-5 text-sky-600" aria-hidden />
          ) : (
            <BedDouble className="h-5 w-5 text-emerald-600" aria-hidden />
          )}
          <span className="tabular-nums">{b.reference}</span>
          <BookingStatusBadge status={b.status} />
        </span>
      }
      description={`${b.title} · ${b.subtitle} · booked ${dateTimeLabel(b.confirmedAt ?? b.createdAt)}`}
      breadcrumbs={
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Ticketing
        </Link>
      }
      actions={
        // Phones: the header's actions slot never shrinks, so pin this row to
        // the <main> content width (viewport minus its px-4) and let it wrap.
        <div className="flex flex-wrap items-center gap-2 max-sm:w-[calc(100vw-2rem)]">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={refresh}
            leftIcon={
              <RefreshCw className={`h-4 w-4 ${booking.isFetching ? 'animate-spin' : ''}`} />
            }
          >
            Refresh
          </UzButton>
          {confirmed && b.product === 'FLIGHT' && (
            <UzButton
              variant="outline"
              onClick={() => id && downloadEticket.mutate(id)}
              loading={downloadEticket.isPending}
              leftIcon={<Download className="h-4 w-4" />}
            >
              E-ticket
            </UzButton>
          )}
          {confirmed && (
            <>
              <UzButton
                variant="outline"
                onClick={() => setResend(true)}
                leftIcon={<Mail className="h-4 w-4" />}
              >
                Resend confirmation
              </UzButton>
              <UzButton
                variant="outline"
                onClick={() => setCancel(true)}
                leftIcon={<Ban className="h-4 w-4" />}
              >
                Cancel booking
              </UzButton>
            </>
          )}
          {canRefund && (
            <UzButton
              variant={onHold ? 'outline' : 'default'}
              onClick={() => setRefund(true)}
              leftIcon={<Undo2 className="h-4 w-4" />}
            >
              {onHold ? 'Refund instead' : 'Refund guest'}
            </UzButton>
          )}
          {onHold && (
            <UzButton
              onClick={() => setBookNow(true)}
              leftIcon={<CheckCircle2 className="h-4 w-4" />}
            >
              Book now
            </UzButton>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {onHold && b.hold && (
          <UzAlert
            tone="warning"
            icon={<Clock className="h-4 w-4" />}
            title={`On hold - waiting ${agoLabel(b.hold.since)} (since ${dateTimeLabel(b.hold.since)})`}
            description={
              <div className="mt-1 flex flex-col gap-1 text-xs">
                <div>
                  The guest paid {money(b.sellPaise, b.currencyCode)} and sees "processing". The
                  supplier refused the booking for <strong>our</strong> account, not theirs.
                </div>
                {b.hold.opsReason && (
                  <div>
                    <span className="font-semibold">Supplier said:</span> {b.hold.opsReason}
                  </div>
                )}
                <div>
                  Nothing happens on its own. Fix the cause (wallet top-up or credentials), then
                  press <strong>Book now</strong> - or <strong>Refund instead</strong>. Admins are
                  reminded again {untilLabel(b.hold.nextReminderAt)}.
                  {b.hold.attempts > 1
                    ? ` This booking has been held ${b.hold.attempts} times.`
                    : ''}
                </div>
              </div>
            }
          />
        )}
        {b.status === 'CANCELLATION_REQUESTED' && (
          <UzAlert
            tone="info"
            icon={<Clock className="h-4 w-4" />}
            title="Cancellation with the supplier"
            description="The request is with the supplier and the booking stays live until it answers. You will be notified when it settles, and can refund the guest then."
          />
        )}
        {(b.status === 'CANCELLED' || b.status === 'REFUNDED') && b.cancellation && (
          <UzAlert
            tone={moneyLeft ? 'warning' : 'success'}
            icon={<Ban className="h-4 w-4" />}
            title={
              moneyLeft
                ? `Cancelled by the supplier - ${money(BigInt(b.sellPaise) - BigInt(b.refundedPaise), b.currencyCode)} still with us`
                : 'Cancelled and refunded'
            }
            description={
              <div className="mt-1 flex flex-col gap-1 text-xs">
                <div>
                  Supplier charge{' '}
                  {b.cancellation.actualChargePaise !== null
                    ? money(b.cancellation.actualChargePaise, b.currencyCode)
                    : '-'}
                  , credits us{' '}
                  {b.cancellation.supplierCreditPaise !== null
                    ? money(b.cancellation.supplierCreditPaise, b.currencyCode)
                    : '-'}
                  {b.cancellation.resolvedAt
                    ? ` · settled ${dateTimeLabel(b.cancellation.resolvedAt)}`
                    : ''}
                  {b.cancellation.requestedByAdmin
                    ? ' · raised by an admin'
                    : ' · raised by the guest'}
                </div>
                {moneyLeft && (
                  <div>
                    Press <strong>Refund guest</strong> to decide the amount and send it.
                  </div>
                )}
              </div>
            }
          />
        )}
        {b.status === 'BOOKING' && (
          <UzAlert
            tone="info"
            title="Waiting for the supplier"
            description="The booking has been sent and the outcome is still settling. This page refreshes itself."
          />
        )}
        {b.status === 'BOOK_FAILED' && b.failureReason && (
          <UzAlert tone="destructive" title="Not booked" description={b.failureReason} />
        )}

        {/*
          Two columns that each stack on their own, not two grid rows. With
          rows, the taller Money card set the height of the first row and the
          Travellers card started only after it - a large empty gap under a
          short Flight card. Stacked columns keep every card one gap apart.
        */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            {b.product === 'FLIGHT' ? <FlightCard booking={b} /> : <HotelCard booking={b} />}
            <TravellersCard booking={b} />
          </div>
          <div className="flex flex-col gap-4">
            <MoneyCard booking={b} />
            <ContactCard booking={b} />
          </div>
        </div>

        <TimelineCard events={b.events} />
      </div>

      <BookNowDialog
        open={bookNow}
        onOpenChange={setBookNow}
        bookingId={b.id}
        reference={b.reference}
        currencyCode={b.currencyCode}
        onDone={refresh}
      />
      <RefundDialog
        open={refund}
        onOpenChange={setRefund}
        bookingId={b.id}
        reference={b.reference}
        onDone={refresh}
      />
      <CancelBookingDialog
        open={cancel}
        onOpenChange={setCancel}
        bookingId={b.id}
        reference={b.reference}
        product={b.product}
        onDone={refresh}
      />
      <CancellationNoticeDialog
        open={notice}
        onOpenChange={setNotice}
        bookingId={b.id}
        reference={b.reference}
        cancellation={b.cancellation}
        onRefund={() => setRefund(true)}
      />
      <ResendConfirmationDialog
        open={resend}
        onOpenChange={setResend}
        bookingId={b.id}
        reference={b.reference}
        product={b.product}
        contactEmail={b.contact.email}
        onDone={refresh}
      />
    </PageShell>
  );
};

// ---------------------------------------------------------------------------

function FlightCard({ booking: b }: { booking: TravelBookingAdminDetail }): React.JSX.Element {
  const f = b.flight;
  return (
    <UzCard
      title="Flight"
      description={`${f?.isLcc ? 'LCC' : 'GDS'} · ${f?.cabinClass ?? 'ECONOMY'} · ${b.travellerCount} traveller${b.travellerCount === 1 ? '' : 's'}`}
      actions={
        f?.pnr ? (
          <UzBadge tone="secondary" pill>
            PNR {f.pnr}
          </UzBadge>
        ) : null
      }
    >
      <div className="flex flex-col gap-3">
        {(f?.legs ?? []).map((leg, i) => (
          <div key={i} className="rounded-md border border-zinc-200 p-3">
            <div className="mb-2 text-[11px] font-medium tracking-wide text-zinc-500 uppercase">
              Leg {i + 1}
            </div>
            <div className="flex flex-col gap-2">
              {leg.map((seg, j) => (
                <SegmentRow key={j} segment={seg} />
              ))}
            </div>
          </div>
        ))}
        <UzDescriptionList
          layout="inline"
          density="compact"
          items={[
            {
              term: 'Ticket numbers',
              description: <span className="break-all">{f?.ticketNumbers.join(', ') || '-'}</span>,
            },
            {
              term: 'Supplier booking id',
              description: <span className="break-all">{f?.tboBookingId ?? '-'}</span>,
            },
            { term: 'Last ticketing date', description: dateTimeLabel(f?.lastTicketDate) },
            {
              term: 'Travel',
              description: `${dateLabel(b.travelStartDate)} to ${dateLabel(b.travelEndDate)}`,
            },
          ]}
        />
      </div>
    </UzCard>
  );
}

/** One supplier segment, read straight off the stored snapshot. */
function SegmentRow({ segment }: { segment: Record<string, unknown> }): React.JSX.Element {
  const obj = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const str = (v: unknown): string =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const airline = obj(segment.Airline ?? segment.airline);
  const origin = obj(segment.Origin ?? segment.origin);
  const destination = obj(segment.Destination ?? segment.destination);
  const oAir = obj(origin.Airport ?? origin.airport);
  const dAir = obj(destination.Airport ?? destination.airport);
  const code =
    `${str(airline.AirlineCode ?? airline.airlineCode)} ${str(airline.FlightNumber ?? airline.flightNumber)}`.trim();
  const name = str(airline.AirlineName ?? airline.airlineName);
  const dep = str(origin.DepTime ?? origin.depTime);
  const arr = str(destination.ArrTime ?? destination.arrTime);
  return (
    <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="min-w-0 font-medium wrap-break-word text-zinc-900">
        {str(oAir.AirportCode ?? oAir.airportCode) || str(oAir.CityName)} →{' '}
        {str(dAir.AirportCode ?? dAir.airportCode) || str(dAir.CityName)}
        <span className="ml-2 text-xs font-normal text-zinc-500">
          {code}
          {name ? ` · ${name}` : ''}
        </span>
      </div>
      <div className="text-xs text-zinc-600 tabular-nums">
        {dep ? dateTimeLabel(dep) : '-'} → {arr ? dateTimeLabel(arr) : '-'}
      </div>
    </div>
  );
}

function HotelCard({ booking: b }: { booking: TravelBookingAdminDetail }): React.JSX.Element {
  const h = b.hotel;
  return (
    <UzCard
      title={h?.hotelName ?? 'Hotel'}
      description={h?.address ?? undefined}
      actions={
        h?.confirmationNo ? (
          <UzBadge tone="secondary" pill>
            Conf. {h.confirmationNo}
          </UzBadge>
        ) : null
      }
    >
      <div className="flex flex-col gap-3">
        <UzDescriptionList
          layout="inline"
          density="compact"
          items={[
            { term: 'Check-in', description: dateLabel(h?.checkIn) },
            { term: 'Check-out', description: dateLabel(h?.checkOut) },
            { term: 'Nights', description: String(h?.nights ?? '-') },
            { term: 'Star rating', description: h?.starRating ? `${h.starRating}★` : '-' },
            { term: 'Guest nationality', description: h?.guestNationality ?? '-' },
            { term: 'Supplier status', description: h?.supplierStatus ?? '-' },
            {
              term: 'Supplier booking id',
              description: <span className="break-all">{h?.supplierBookingId ?? '-'}</span>,
            },
            {
              term: 'Cancellation',
              description: h?.isRefundable
                ? `Refundable${h.lastCancellationDate ? ` until ${dateTimeLabel(h.lastCancellationDate)}` : ''}`
                : 'Non-refundable',
            },
          ]}
        />
        <div className="flex flex-col gap-2">
          {(h?.rooms ?? []).map((room) => (
            <div key={room.roomNumber} className="rounded-md border border-zinc-200 p-3 text-sm">
              <div className="font-medium text-zinc-900">
                Room {room.roomNumber}: {room.name}
              </div>
              <div className="text-xs text-zinc-600">
                {room.mealType ?? 'Room only'} · {room.adults} adult{room.adults === 1 ? '' : 's'}
                {room.children > 0
                  ? `, ${room.children} child${room.children === 1 ? '' : 'ren'}`
                  : ''}
              </div>
            </div>
          ))}
        </div>
        {h?.specialRequests && (
          <div className="rounded-md bg-zinc-50 p-3 text-xs text-zinc-700">
            <span className="font-semibold">Special requests:</span> {h.specialRequests}
          </div>
        )}
      </div>
    </UzCard>
  );
}

/** A labelled money row; `strong` bolds a subtotal, `hint` adds a muted note. */
function MoneyRow({
  label,
  value,
  strong,
  tone,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
  tone?: 'plus' | 'minus' | 'muted';
  hint?: string;
}): React.JSX.Element {
  const toneClass = tone === 'plus' ? 'text-emerald-700' : tone === 'minus' ? 'text-red-700' : '';
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className={`min-w-0 text-zinc-600 ${strong ? 'font-semibold text-zinc-900' : ''}`}>
        {label}
        {hint ? <span className="ml-1 text-xs text-zinc-400">{hint}</span> : null}
      </span>
      <span
        className={`shrink-0 whitespace-nowrap tabular-nums ${strong ? 'font-semibold text-zinc-900' : ''} ${toneClass}`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * The Add-ons line with a chevron that expands to the per-item prices (seat
 * 12A ₹250, seat 12B ₹500, a meal, …) read from the supplier ticketing
 * request. No chevron when there is no breakdown to show.
 */
function AddonsRow({
  total,
  addons,
  fmt,
}: {
  total: bigint;
  addons: TravelBookingAdminDetail['addons'];
  fmt: (v: bigint) => string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const has = addons.length > 0;
  return (
    <>
      <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
        <button
          type="button"
          disabled={!has}
          onClick={() => setOpen((o) => !o)}
          className={`flex min-w-0 items-center gap-1 text-left text-zinc-600 ${has ? 'hover:text-zinc-900' : 'cursor-default'}`}
        >
          {has ? (
            open ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            )
          ) : null}
          <span>Add-ons (seats)</span>
        </button>
        <span className="shrink-0 whitespace-nowrap tabular-nums">{fmt(total)}</span>
      </div>
      {open && has && (
        <div className="mt-0.5 mb-1 ml-5 flex flex-col gap-0.5">
          {addons.map((a, i) => (
            <div
              key={`${a.kind}-${a.code}-${i}`}
              className="flex items-baseline justify-between gap-3 text-xs text-zinc-500"
            >
              <span className="min-w-0 truncate">
                {a.kind} {a.code}
                {a.paxName ? ` · ${a.paxName}` : ''}
              </span>
              <span className="shrink-0 whitespace-nowrap tabular-nums">
                {fmt(BigInt(a.pricePaise))}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function MoneySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
        {title}
      </div>
      {children}
    </div>
  );
}

function MoneyCard({ booking: b }: { booking: TravelBookingAdminDetail }): React.JSX.Element {
  const m = b.money;
  const c = m.currencyCode;
  const toBig = (s: string | null | undefined): bigint | null => (s == null ? null : BigInt(s));
  const fmt = (v: bigint): string => money(v.toString(), c);

  const ssr = BigInt(m.ssrPaise);
  const markup = BigInt(m.markupPaise);
  const conv = BigInt(m.convenienceFeePaise);
  const gst = BigInt(m.gstPaise);
  const tcs = BigInt(m.tcsPaise);
  const quote = BigInt(m.supplierNetMinor);
  const actual = toBig(m.actualSupplierNetMinor);
  const published = toBig(m.supplierPublishedMinor);
  const fee = toBig(m.gatewayFeeMinor);
  const absorbed = BigInt(m.absorbedDeltaPaise);

  // What TBO actually took (airfare + seats). Falls back to the quote for
  // bookings placed before the actual was captured.
  const paidToTbo = actual ?? quote;
  // Published fare includes seats, the quote does not - their gap is the seat
  // cost the supplier charged us.
  const seatCost = published != null ? published - quote : null;
  // Published over what we were actually billed = the agency discount TBO gives us.
  const agentDiscount = published != null && actual != null ? published - actual : null;
  // What we made on the add-ons: what we charged the guest minus what they cost us.
  const addonMargin = seatCost != null ? ssr - seatCost : null;
  // Profit before the gateway takes its cut, GST excluded (GST is the govt's).
  const grossProfit = markup + (agentDiscount ?? 0n) + (addonMargin ?? 0n);
  // The one net-profit definition, shared with the ticketing list and summary.
  const netProfit = BigInt(
    bookingNetProfitPaise({
      markupPaise: m.markupPaise,
      ssrPaise: m.ssrPaise,
      supplierNetMinor: m.supplierNetMinor,
      actualSupplierNetMinor: m.actualSupplierNetMinor,
      supplierPublishedMinor: m.supplierPublishedMinor,
      gatewayFeeMinor: m.gatewayFeeMinor,
    }),
  );

  return (
    <UzCard title="Money" description="What the guest paid, what it cost us, and what we kept.">
      <div className="flex flex-col gap-4">
        <MoneySection title="What the guest paid">
          <MoneyRow label="Base fare" value={fmt(BigInt(m.basePaise))} />
          <MoneyRow label="Taxes" value={fmt(BigInt(m.taxPaise))} />
          {ssr > 0n && <AddonsRow total={ssr} addons={b.addons} fmt={fmt} />}
          <MoneyRow label="Our markup" value={fmt(markup)} />
          {conv > 0n && <MoneyRow label="Convenience fee" value={fmt(conv)} />}
          <MoneyRow label="GST" value={fmt(gst)} />
          {tcs > 0n && <MoneyRow label="TCS" value={fmt(tcs)} />}
          {m.roundOffPaise !== '0' && (
            <MoneyRow label={ROUND_OFF_LABEL} value={signedMoney(m.roundOffPaise, c)} />
          )}
          <MoneyRow label="Guest paid" value={fmt(BigInt(m.sellPaise))} strong />
        </MoneySection>

        <MoneySection title="What it cost us">
          <MoneyRow
            label="Paid to TBO"
            value={fmt(paidToTbo)}
            hint={actual == null ? '(quote)' : undefined}
          />
          <MoneyRow
            label="Razorpay fee"
            value={fee != null ? fmt(fee) : '--'}
            hint={fee != null ? 'incl its GST' : 'not captured yet'}
          />
          <MoneyRow label="GST remitted" value={fmt(gst)} hint="collected for govt, not ours" />
        </MoneySection>

        <MoneySection title="What we earned">
          <MoneyRow label="Our markup" value={fmt(markup)} tone="plus" />
          <MoneyRow
            label="TBO agent discount"
            value={agentDiscount != null ? fmt(agentDiscount) : '--'}
            tone={agentDiscount != null ? 'plus' : undefined}
            hint={agentDiscount == null ? 'not captured' : undefined}
          />
          {addonMargin != null && addonMargin !== 0n && (
            <MoneyRow
              label="Add-on margin"
              value={fmt(addonMargin)}
              tone={addonMargin > 0n ? 'plus' : 'minus'}
            />
          )}
          <MoneyRow label="Profit (excl GST)" value={fmt(grossProfit)} strong />
          {fee != null && fee > 0n && (
            <MoneyRow label="less Razorpay fee" value={`-${fmt(fee)}`} tone="minus" />
          )}
          <MoneyRow
            label="Net profit"
            value={fmt(netProfit)}
            strong
            tone={netProfit >= 0n ? 'plus' : 'minus'}
          />
          {absorbed !== 0n && (
            <MoneyRow
              label="Absorbed on re-issue"
              value={signedMoney(m.absorbedDeltaPaise, c)}
              tone={absorbed > 0n ? 'minus' : 'plus'}
            />
          )}
          {BigInt(m.refundedPaise) > 0n && (
            <MoneyRow label="Refunded" value={fmt(BigInt(m.refundedPaise))} />
          )}
        </MoneySection>

        <MoneySection title="Payment">
          <MoneyRow label="Gateway" value={b.payment.provider ?? '-'} />
          <MoneyRow
            label="Payment id"
            value={
              <span className="font-mono text-xs break-all">{b.payment.paymentId ?? '-'}</span>
            }
          />
          <MoneyRow label="Paid at" value={dateTimeLabel(b.payment.paidAt)} />
        </MoneySection>
      </div>
    </UzCard>
  );
}

function ContactCard({ booking: b }: { booking: TravelBookingAdminDetail }): React.JSX.Element {
  const c = b.contact;
  const gst = b.gst as { gstNumber?: unknown; companyName?: unknown } | null;
  return (
    <UzCard title="Contact" description="As entered on the traveller details page.">
      <UzDescriptionList
        layout="stacked"
        density="compact"
        items={[
          {
            term: 'Email',
            description: (
              <a className="break-all text-blue-700 hover:underline" href={`mailto:${c.email}`}>
                {c.email}
              </a>
            ),
          },
          {
            term: 'Phone',
            description: (
              <a className="break-all text-blue-700 hover:underline" href={`tel:${c.phone}`}>
                {c.phone}
              </a>
            ),
          },
          {
            term: 'Address',
            description: [c.addressLine, c.city, c.countryCode].filter(Boolean).join(', ') || '-',
          },
          ...(gst && (typeof gst.gstNumber === 'string' || typeof gst.companyName === 'string')
            ? [
                {
                  term: 'GST',
                  description: [gst.companyName, gst.gstNumber]
                    .filter((v): v is string => typeof v === 'string' && v.length > 0)
                    .join(' · '),
                },
              ]
            : []),
        ]}
      />
    </UzCard>
  );
}

function TimelineCard({ events }: { events: TravelBookingAdminEvent[] }): React.JSX.Element {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const ordered = [...events].reverse();
  return (
    <UzCard
      title="Timeline"
      description="Every status change and every supplier call, newest first. Supplier payloads are redacted."
    >
      <ol className="divide-y divide-zinc-100">
        {ordered.map((e) => {
          const hasDetail = e.detail !== null && e.detail !== undefined;
          const expanded = open.has(e.id);
          const tone =
            e.kind === 'ON_HOLD' || e.kind === 'REFUND_FAILED' || e.kind === 'BOOK_FAILED'
              ? 'text-red-700'
              : e.kind === 'CONFIRMED'
                ? 'text-emerald-700'
                : 'text-zinc-900';
          return (
            <li key={e.id} className="py-2.5">
              <button
                type="button"
                onClick={() => hasDetail && toggle(e.id)}
                className={`flex w-full items-start gap-2 text-left ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className="mt-0.5 shrink-0 text-zinc-400">
                  {hasDetail ? (
                    expanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )
                  ) : (
                    <span className="inline-block h-3.5 w-3.5" />
                  )}
                </span>
                {/* Phones: the timestamp drops under the label instead of
                    squeezing it into a sliver next to the date. */}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <span className="min-w-0">
                    <span className={`text-sm font-medium ${tone}`}>{eventLabel(e.kind)}</span>
                    {(e.fromStatus || e.toStatus) && (
                      <span className="ml-2 text-xs text-zinc-500">
                        {e.fromStatus ?? '·'} → {e.toStatus ?? '·'}
                      </span>
                    )}
                    {e.errorMessage && (
                      <span className="mt-0.5 block text-xs wrap-break-word text-zinc-600">
                        {e.errorMessage}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-zinc-500 tabular-nums sm:text-right">
                    {dateTimeLabel(e.createdAt)}
                  </span>
                </span>
              </button>
              {expanded && hasDetail && (
                <div className="mt-2 min-w-0 rounded-md border border-zinc-200 bg-zinc-50 p-3 sm:ml-5">
                  <EventDetail value={e.detail} />
                </div>
              )}
            </li>
          );
        })}
        {ordered.length === 0 && (
          <li className="py-6 text-center text-sm text-zinc-500">No events recorded.</li>
        )}
      </ol>
    </UzCard>
  );
}

/** Keys that hold whole supplier payloads. Big, redacted, rarely needed - kept behind a toggle. */
const RAW_KEYS = new Set(['request', 'response', 'error', 'raw', 'detail']);

/** Keys whose values are paise amounts (whole-number strings). */
const MONEY_KEY = /(paise|sell|delta|amount|fare)$/i;

/** "publishedFare" -> "Published fare", "old_sell" -> "Old sell". */
function humaniseKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function scalarLabel(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number')
    return MONEY_KEY.test(key) ? money(Math.round(value)) : value.toLocaleString('en-IN');
  if (typeof value === 'string') {
    if (MONEY_KEY.test(key) && /^-?\d+$/.test(value)) return money(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return dateTimeLabel(value);
    return value;
  }
  return String(value);
}

/**
 * An event's detail, as a person reads it: one row per field, money as
 * money, dates as dates, and the supplier's full request/response payloads
 * folded away behind "Show raw" because they are for a dispute, not a glance.
 */
function EventDetail({ value, depth = 0 }: { value: unknown; depth?: number }): React.JSX.Element {
  if (value === null || value === undefined)
    return <span className="text-xs text-zinc-500">-</span>;
  if (Array.isArray(value)) {
    if (value.every((v) => v === null || typeof v !== 'object')) {
      return (
        <span className="text-xs text-zinc-800">
          {value.map((v) => scalarLabel('', v)).join(', ') || '-'}
        </span>
      );
    }
    return (
      <ol className="flex flex-col gap-2">
        {value.map((item, i) => (
          <li key={i} className="rounded border border-zinc-200 bg-white p-2">
            <div className="mb-1 text-[10px] font-semibold tracking-wide text-zinc-500 uppercase">
              Item {i + 1}
            </div>
            <EventDetail value={item} depth={depth + 1} />
          </li>
        ))}
      </ol>
    );
  }
  if (typeof value !== 'object') {
    return <span className="text-xs text-zinc-800">{scalarLabel('', value)}</span>;
  }

  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([, v]) => v !== null && v !== undefined,
  );
  if (entries.length === 0) return <span className="text-xs text-zinc-500">-</span>;

  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[minmax(120px,max-content)_1fr]">
      {entries.map(([key, v]) => {
        const nested = typeof v === 'object';
        const raw = RAW_KEYS.has(key) || depth >= 2;
        return (
          <React.Fragment key={key}>
            <dt className="text-zinc-500">{humaniseKey(key)}</dt>
            <dd className="min-w-0 break-words text-zinc-800">
              {!nested ? (
                scalarLabel(key, v)
              ) : raw ? (
                <details>
                  <summary className="cursor-pointer text-blue-700 hover:underline">
                    Show raw
                  </summary>
                  <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-zinc-900 p-3 text-[11px] leading-relaxed text-zinc-100">
                    {JSON.stringify(v, null, 2)}
                  </pre>
                </details>
              ) : (
                <EventDetail value={v} depth={depth + 1} />
              )}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}
